import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SocialBadges from '@/components/SocialBadges';
import FormulesDuJour from '@/components/FormulesDuJour';
import { publicApi } from '@/lib/api';
import { enrichHours, formatDaySlots } from '@/lib/hours';
import { SOCIAL_ICON_MAP } from '@/components/SocialBadges';
import { Button } from '@/components/ui/button';
import { Star, MapPin, UtensilsCrossed, TreePine, Euro, Phone, Navigation, Calendar, Facebook, Instagram, AlertTriangle, X, MessageCircle, Globe } from 'lucide-react';
import { useLanguage } from '@/lib/i18n';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Home() {
  const [settings, setSettings] = useState({});
  const [hours, setHours] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [actus, setActus] = useState([]);
  const [sections, setSections] = useState([]);
  const [formules, setFormules] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    let loaded = 0;
    const checkDone = () => { loaded++; if (loaded >= 3) setInitialLoading(false); };
    publicApi.getSettings().then(r => setSettings(r.data)).catch(() => {}).finally(checkDone);
    publicApi.getHours().then(r => {
      setHours(enrichHours(r.data.hours));
      const today = new Date().toISOString().split('T')[0];
      const upcoming = (r.data.exceptions || []).filter(e => e.date >= today);
      setExceptions(upcoming);
    }).catch(() => {}).finally(checkDone);
    Promise.all([
      publicApi.getActus().then(r => setActus(r.data || [])).catch(() => {}),
      publicApi.getHomepageSections().then(r => setSections(r.data || [])).catch(() => {}),
      publicApi.getMenu().then(r => setFormules(r.data.formules || [])).catch(() => {}),
    ]).finally(checkDone);
  }, []);

  // Get section by slug
  const getSection = (slug) => sections.find(s => s.slug === slug) || {};

  // Textes par défaut si non définis dans settings
  const heroSection = getSection('hero');
  const cuisineSection = getSection('cuisine');
  const terrasseSection = getSection('terrasse');
  
  const heroTitle = heroSection.title || settings.hero_title || t('home.defaultHeroTitle');
  const heroSubtitle = heroSection.description || settings.hero_subtitle || t('home.defaultHeroSubtitle');
  const heroImage = heroSection.image_url || 'https://images.unsplash.com/photo-1759156241324-6343c446a27c?w=1600&q=80';
  
  const aboutTitle = cuisineSection.title || settings.about_title || t('home.defaultAboutTitle');
  const aboutText = cuisineSection.description || settings.about_text || t('home.defaultAboutText');
  const aboutImage = cuisineSection.image_url || 'https://images.unsplash.com/photo-1628155752640-c9d26feb09f9?w=800&q=80';
  const aboutTags = settings.about_tags || ['Burgers maison', 'Produits frais', 'Tapas', 'Formules dès 15 €'];
  
  const atmosphereTitle = terrasseSection.title || settings.atmosphere_title || t('home.defaultAtmosphereTitle');
  const atmosphereText = terrasseSection.description || settings.atmosphere_text || t('home.defaultAtmosphereText');
  const atmosphereImage = terrasseSection.image_url || 'https://images.unsplash.com/photo-1769168375647-843e2e822035?w=800&q=80';
  
  const ctaTitle = settings.cta_title || t('home.defaultCtaTitle');
  const ctaText = settings.cta_text || t('home.defaultCtaText');
  const btnReserve = settings.btn_reserve || t('home.defaultBtnReserve');
  const btnMenu = settings.btn_menu || t('home.defaultBtnMenu');
  const btnCall = settings.btn_call || t('home.defaultBtnCall');
  const btnDirections = settings.btn_directions || t('home.defaultBtnDirections');
  
  // Check if orders module is enabled
  const ordersEnabled = settings.ordersModuleEnabled === true;

  // Featured formules: use admin selection or first 2-3
  const featuredIds = settings.featured_formules || [];
  const featuredFormules = featuredIds.length > 0
    ? formules.filter(f => featuredIds.includes(f.id))
    : formules.slice(0, 2);

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-cream" data-testid="home-page-loading">
        <Navbar />
        <div className="h-[90vh] min-h-[600px] flex items-center justify-center bg-garden-900">
          <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="home-page">
      <Navbar />
      <SocialBadges settings={settings} />

      {/* Hero Section */}
      <section className="relative h-[90vh] min-h-[600px] flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Terrasse Côté Terrasse Bordeaux"
            className="w-full h-full object-cover"
          />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <span className="font-accent text-sunny text-3xl md:text-5xl block mb-4 opacity-0 animate-fade-up stagger-1">
            {settings.name || 'Côté Terrasse'}
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mb-6 opacity-0 animate-fade-up stagger-2">
            {heroTitle}
          </h1>
          <p className="text-lg md:text-xl text-garden-200 mb-10 opacity-0 animate-fade-up stagger-3 max-w-2xl mx-auto">
            {heroSubtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center opacity-0 animate-fade-up stagger-4">
            <Link to="/reservation">
              <Button data-testid="hero-reserve-btn" className="bg-sunny text-charcoal hover:bg-yellow-300 rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:scale-105 transition-all">
                {btnReserve}
              </Button>
            </Link>
            <Link to="/carte">
              <Button data-testid="hero-menu-btn" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-garden-800 rounded-full px-8 py-6 text-lg font-medium">
                {btnMenu}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Info Bar */}
      <section className="bg-garden-800 text-white py-5">
        <div className="max-w-7xl mx-auto px-4 flex flex-wrap justify-center gap-6 md:gap-12 text-sm">
          <div className="flex items-center gap-2">
            <Star size={16} className="text-sunny fill-sunny" />
            <span>{t('home.googleRating')}</span>
          </div>
          <div className="flex items-center gap-2">
            <Euro size={16} className="text-sunny" />
            <span>{t('home.formulesFrom')}</span>
          </div>
          <a href={settings.maps_url || '#'} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-sunny transition-colors">
            <MapPin size={16} className="text-sunny" />
            <span>{settings.address?.split(',')[0] || '198 Cours de l\'Argonne'}</span>
          </a>
          <a href={`tel:${(settings.phone || '').replace(/\s/g, '')}`} className="flex items-center gap-2 hover:text-sunny transition-colors md:hidden">
            <Phone size={16} className="text-sunny" />
            <span>{btnCall}</span>
          </a>
        </div>
      </section>

      {/* Notre Cuisine */}
      <section className="py-20 md:py-28 noise-overlay relative" data-testid="cuisine-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-sm font-medium tracking-wide uppercase text-garden-500 mb-2 block">{t('home.sectionCuisine')}</span>
              <h2 className="text-4xl md:text-5xl font-heading font-semibold text-garden-800 mb-6">
                {aboutTitle}
              </h2>
              {aboutText.split('\\n').map((para, i) => (
                <p key={`about-p-${i}`} className="text-base md:text-lg text-charcoal/80 leading-relaxed mb-4">
                  {para}
                </p>
              ))}
              <div className="flex flex-wrap gap-3 mt-6">
                {aboutTags.map((tag) => (
                  <span key={tag} className="bg-garden-100 text-garden-800 px-4 py-2 rounded-full text-sm font-medium">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src={aboutImage}
                alt="Burger maison Côté Terrasse Bordeaux"
                className="rounded-3xl shadow-xl w-full object-cover aspect-[4/5]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Atmosphère */}
      <section className="py-20 md:py-28 bg-garden-800 text-white relative overflow-hidden" data-testid="atmosphere-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-2 md:order-1">
              <img
                src={atmosphereImage}
                alt="Terrasse côté jardin Côté Terrasse Bordeaux"
                className="rounded-3xl shadow-xl w-full object-cover aspect-[4/5]"
              />
            </div>
            <div className="order-1 md:order-2">
              <span className="text-sm font-medium tracking-wide uppercase text-garden-400 mb-2 block">{t('home.sectionAtmosphere')}</span>
              <h2 className="text-4xl md:text-5xl font-heading font-semibold text-white mb-6">
                {atmosphereTitle}
              </h2>
              {atmosphereText.split('\\n').map((para, i) => (
                <p key={`atmo-p-${i}`} className="text-base md:text-lg text-garden-200 leading-relaxed mb-4">
                  {para}
                </p>
              ))}
              <Link to="/galerie">
                <Button data-testid="hero-gallery-btn" className="bg-sunny text-charcoal hover:bg-yellow-300 rounded-full px-8 py-4 font-medium hover:scale-105 transition-all mt-4">
                  {t('home.seeGallery')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Formules */}
      <FormulesDuJour />
          {formules.length > 0 && (
      <section className="py-20 md:py-28 noise-overlay relative" data-testid="formules-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-sm font-medium tracking-wide uppercase text-garden-500 mb-2 block">{t('home.sectionFormules')}</span>
          <h2 className="text-4xl md:text-5xl font-heading font-semibold text-garden-800 mb-12">
            {t('home.formulesSubtitle')}
          </h2>
          <div className={`grid gap-8 max-w-4xl mx-auto ${featuredFormules.length === 1 ? 'max-w-md' : featuredFormules.length === 2 ? 'sm:grid-cols-2 max-w-3xl' : 'sm:grid-cols-2 lg:grid-cols-3'}`}>
            {featuredFormules.map((f) => (
              <div key={f.id} className="bg-white rounded-3xl p-8 border border-garden-800/5 shadow-lg hover:shadow-xl transition-shadow" data-testid={`formule-card-${f.id}`}>
                <div className="text-5xl font-heading font-bold text-garden-800 mb-2">{f.price}€</div>
                <h3 className="font-heading font-semibold text-xl text-garden-800 mb-4">{f.name}</h3>
                {f.description && <p className="text-charcoal/60 text-sm mb-3">{f.description}</p>}
                <ul className="text-charcoal/80 text-sm space-y-2">
                  {(f.items || []).map((item, i) => <li key={`${f.id}-li-${i}`}>{item}</li>)}
                </ul>
              </div>
            ))}
          </div>
          <Link to="/carte" className="inline-block mt-10">
            <Button variant="outline" className="border-2 border-garden-800 text-garden-800 hover:bg-garden-800 hover:text-white rounded-full px-8 py-4 font-medium">
              {t('home.seeFullMenu')}
            </Button>
          </Link>
        </div>
      </section>
      )}

      {/* Actualités */}
      {actus.length > 0 && (
        <section className="py-16 bg-sunny/30" data-testid="actus-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <span className="text-sm font-medium tracking-wide uppercase text-garden-500 mb-2 block">{t('home.sectionActus')}</span>
              <h2 className="text-3xl md:text-4xl font-heading font-semibold text-garden-800">{t('home.actusSubtitle')}</h2>
            </div>
            <div className={`grid gap-6 ${actus.length === 1 ? 'max-w-xl mx-auto' : 'sm:grid-cols-2 lg:grid-cols-3'}`}>
              {actus.slice(0, 3).map(actu => (
                <div key={actu.id} className="bg-white rounded-2xl overflow-hidden border border-garden-800/5 shadow-md hover:shadow-xl transition-all hover:-translate-y-1 group" data-testid={`actu-home-${actu.id}`}>
                  {actu.image_url && (
                    <div className="h-48 overflow-hidden">
                      <img src={actu.image_url.startsWith('/') ? `${API}${actu.image_url}` : actu.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <div className="p-6">
                    <span className="text-xs text-garden-500 font-medium">{formatDate(actu.created_at)}</span>
                    <h3 className="font-heading font-semibold text-lg text-garden-800 mt-1 mb-2">{actu.title}</h3>
                    <p className="text-sm text-charcoal/70 line-clamp-3">{actu.content}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Horaires exceptionnels */}
      {exceptions.length > 0 && (
        <section className="py-8 bg-amber-50/60" data-testid="exceptions-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle size={18} className="text-amber-600" />
              <h2 className="font-heading font-semibold text-lg text-garden-800">{t('home.sectionExceptions')}</h2>
            </div>
            <div className="flex flex-wrap gap-3">
              {exceptions.map(exc => (
                <div key={exc.id} className="bg-white rounded-xl px-4 py-3 border border-amber-200 shadow-sm flex items-center gap-3" data-testid={`exception-${exc.id}`}>
                  <div className="text-center leading-tight">
                    <div className="text-xs text-charcoal/50 font-medium">{new Date(exc.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'short' })}</div>
                    <div className="text-lg font-bold text-garden-800">{new Date(exc.date + 'T00:00:00').getDate()}</div>
                    <div className="text-xs text-charcoal/50">{new Date(exc.date + 'T00:00:00').toLocaleDateString('fr-FR', { month: 'short' })}</div>
                  </div>
                  <div className="border-l border-garden-200 pl-3">
                    <span className={`text-sm font-medium ${exc.is_closed ? 'text-red-600' : 'text-garden-700'}`}>
                      {exc.is_closed ? t('home.closed') : (exc.slots || []).map(s => `${(s.start||'').replace(':','h')}–${(s.end||'').replace(':','h')}`).join(' / ')}
                    </span>
                    {exc.label && <p className="text-xs text-charcoal/50">{exc.label}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Réseaux sociaux - Suivez-nous */}
      {(() => {
        const socialLinks = [];
        if (settings.show_instagram !== false && settings.social_instagram) socialLinks.push({ key: 'instagram', url: settings.social_instagram, label: 'Instagram', icon: Instagram, cls: 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white' });
        if (settings.show_facebook !== false && settings.social_facebook) socialLinks.push({ key: 'facebook', url: settings.social_facebook, label: 'Facebook', icon: Facebook, cls: 'bg-[#1877F2] text-white' });
        if (settings.show_twitter !== false && settings.social_twitter) socialLinks.push({ key: 'twitter', url: settings.social_twitter, label: 'X', icon: X, cls: 'bg-black text-white' });
        if (settings.show_pinterest !== false && settings.social_pinterest) socialLinks.push({ key: 'pinterest', url: settings.social_pinterest, label: 'Pinterest', icon: MapPin, cls: 'bg-[#E60023] text-white' });
        if (settings.show_threads !== false && settings.social_threads) socialLinks.push({ key: 'threads', url: settings.social_threads, label: 'Threads', icon: MessageCircle, cls: 'bg-black text-white' });
        (settings.custom_socials || []).forEach(c => {
          if (c.url) {
            const info = SOCIAL_ICON_MAP[c.icon_key];
            const CIcon = info ? info.icon : Globe;
            const cls = info ? `${info.bg} text-white` : 'bg-garden-700 text-white';
            socialLinks.push({ key: `custom-${c.name}`, url: c.url, label: c.name, icon: CIcon, cls });
          }
        });
        if (socialLinks.length === 0) return null;
        return (
          <section className="py-12 bg-garden-50" data-testid="social-section">
            <div className="max-w-4xl mx-auto px-4 text-center">
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-garden-800 mb-4">{t('home.sectionSocial')}</h2>
              {settings.social_text && <p className="text-charcoal/70 mb-8 max-w-xl mx-auto">{settings.social_text}</p>}
              <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
                {socialLinks.map(s => {
                  const Icon = s.icon;
                  return (
                    <a key={s.key} href={s.url} target="_blank" rel="noopener noreferrer"
                      className={`flex items-center justify-center gap-2.5 ${s.cls} hover:opacity-90 rounded-full px-6 py-3 transition-all hover:scale-105 shadow-md`}
                      data-testid={`social-${s.key}-link`}>
                      {Icon && <Icon size={20} />}
                      <span className="font-medium">{s.label}</span>
                    </a>
                  );
                })}
              </div>
            </div>
          </section>
        );
      })()}

      {/* CTA */}
      <section className="py-16 bg-sunny" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-charcoal mb-4">
            {ctaTitle}
          </h2>
          <p className="text-charcoal/80 mb-8 text-lg">
            {ctaText}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/reservation">
              <Button data-testid="cta-reserve-btn" className="bg-garden-800 text-white hover:bg-garden-900 rounded-full px-8 py-6 text-lg font-medium shadow-lg">
                {btnReserve}
              </Button>
            </Link>
            <a href={`tel:${(settings.phone || '').replace(/\s/g, '')}`}>
              <Button variant="outline" className="border-2 border-garden-800 text-garden-800 hover:bg-garden-800 hover:text-white rounded-full px-8 py-6 text-lg font-medium">
                <Phone size={18} className="mr-2" /> {btnCall}
              </Button>
            </a>
          </div>
          {/* Quick access buttons for mobile */}
          <div className="flex justify-center gap-4 mt-6 sm:hidden">
            <a href={settings.maps_url || '#'} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-garden-800 font-medium">
              <Navigation size={16} /> {btnDirections}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
